import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, LabelList } from 'recharts';
import { TrendingUp, AlertTriangle, CheckCircle, Clock, Building2, User, Calendar, Send, Pause, FileSearch } from 'lucide-react';
import { toast } from 'sonner';
import { parseLocalDate } from '../utils/dateHelpers';
import './EstadisticasModulo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORES = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#795548', '#607D8B'];
const COLORES_ESTADO = {
  'Registrado': '#2196F3',
  'En proceso de análisis': '#FF9800',
  'Enviado / Compartido': '#9C27B0',
  'En Revisión': '#00BCD4',
  'En espera de otra gestión': '#795548',
  'Completada / Archivada': '#4CAF50'
};
const COLORES_RIESGO = {
  'Alto': '#F44336',
  'Medio': '#FF9800',
  'Bajo': '#4CAF50'
};

const EstadisticasModulo = ({ tipoGestion, colorModulo = '#2196F3' }) => {
  const [loading, setLoading] = useState(true);
  const [gestiones, setGestiones] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    porEstado: [],
    porInstitucion: [],
    porAsesor: [],
    porMes: [],
    vencidas: 0,
    completadas: 0,
    enProceso: 0,
    registradas: 0,
    enEspera: 0,
    enRevision: 0,
    derivadasProbidad: 0
  });

  useEffect(() => {
    fetchGestiones();
  }, [tipoGestion]);

  const fetchGestiones = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gestiones?tipo_gestion=${encodeURIComponent(tipoGestion)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data || [];
      setGestiones(data);
      calcularEstadisticas(data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const calcularEstadisticas = (data) => {
    // Por estado
    const estadoCount = {};
    data.forEach(g => {
      estadoCount[g.estado] = (estadoCount[g.estado] || 0) + 1;
    });
    const porEstado = Object.entries(estadoCount).map(([name, value]) => ({ name, value, fill: COLORES_ESTADO[name] || '#757575' }));

    // Por institución (top 10)
    const instCount = {};
    data.forEach(g => {
      if (g.institucion) {
        instCount[g.institucion] = (instCount[g.institucion] || 0) + 1;
      }
    });
    const porInstitucion = Object.entries(instCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, casos], i) => ({ name: name.length > 30 ? name.substring(0, 30) + '...' : name, casos, fill: COLORES[i % COLORES.length] }));

    // Por asesor
    const asesorCount = {};
    data.forEach(g => {
      if (g.nombre_asesor_cnc) {
        asesorCount[g.nombre_asesor_cnc] = (asesorCount[g.nombre_asesor_cnc] || 0) + 1;
      }
    });
    const porAsesor = Object.entries(asesorCount)
      .sort((a, b) => b[1] - a[1])
      .map(([name, casos], i) => ({ name, casos, fill: COLORES[i % COLORES.length] }));

    // Por mes (últimos 6 meses)
    const mesCount = {};
    const ahora = new Date();
    for (let i = 5; i >= 0; i--) {
      const fecha = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
      const mes = fecha.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
      mesCount[mes] = 0;
    }
    data.forEach(g => {
      const fecha = new Date(g.fecha_asignacion);
      const mes = fecha.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
      if (mesCount.hasOwnProperty(mes)) {
        mesCount[mes]++;
      }
    });
    const porMes = Object.entries(mesCount).map(([name, casos]) => ({ name, casos }));

    // Vencidas CNC - NO incluir las que ya fueron derivadas a Probidad (atendidas en tiempo)
    // Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const vencidasCNC = data.filter(g => {
      if (!g.fecha_vencimiento) return false;
      if (g.estado === 'Completada / Archivada') return false;
      // Si ya fue derivada a Probidad, NO es vencida para CNC (se atendió en tiempo)
      if (g.asesor_probidad_id || g.asesor_probidad_nombre) return false;
      const fechaVenc = parseLocalDate(g.fecha_vencimiento);
      return fechaVenc && hoy > fechaVenc;
    }).length;
    
    // Vencidas Probidad - Solo las derivadas que tienen fecha vencimiento probidad pasada
    const vencidasProbidad = data.filter(g => {
      if (g.estado === 'Completada / Archivada') return false;
      const yaDerivada = g.asesor_probidad_id || g.asesor_probidad_nombre;
      if (!yaDerivada) return false;
      
      // Si tiene fecha_vencimiento_probidad, usar esa
      if (g.fecha_vencimiento_probidad) {
        const fechaVenc = parseLocalDate(g.fecha_vencimiento_probidad);
        return fechaVenc && hoy > fechaVenc;
      }
      return false;
    }).length;
    
    // Derivadas a Probidad (tienen asesor de probidad asignado)
    const derivadasProbidad = data.filter(g => g.asesor_probidad_nombre).length;

    setStats({
      total: data.length,
      porEstado,
      porInstitucion,
      porAsesor,
      porMes,
      vencidasCNC,
      vencidasProbidad,
      completadas: estadoCount['Completada / Archivada'] || 0,
      enProceso: estadoCount['En proceso de análisis'] || 0,
      registradas: estadoCount['Registrado'] || 0,
      enEspera: estadoCount['En espera de otra gestión'] || 0,
      enRevision: estadoCount['En Revisión'] || 0,
      derivadasProbidad
    });
  };

  if (loading) {
    return (
      <div className="stats-loading">
        <div className="loader"></div>
        <p>Cargando estadísticas...</p>
      </div>
    );
  }

  if (gestiones.length === 0) {
    return (
      <div className="stats-empty">
        <TrendingUp size={48} style={{ opacity: 0.3 }} />
        <p>No hay datos suficientes para mostrar estadísticas</p>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>Registre gestiones para ver las estadísticas del módulo</p>
      </div>
    );
  }

  return (
    <div className="estadisticas-modulo">
      {/* KPIs */}
      <div className="stats-kpi-grid">
        <div className="stats-kpi-card" style={{ borderLeftColor: colorModulo }}>
          <div className="kpi-icon" style={{ backgroundColor: `${colorModulo}20` }}>
            <TrendingUp size={24} style={{ color: colorModulo }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.total}</span>
            <span className="kpi-label">Total Gestiones</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#2196F3' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#E3F2FD' }}>
            <Clock size={24} style={{ color: '#2196F3' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.registradas}</span>
            <span className="kpi-label">Registradas</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#FF9800' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#FFF3E0' }}>
            <AlertTriangle size={24} style={{ color: '#FF9800' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.enProceso}</span>
            <span className="kpi-label">En Análisis</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#00BCD4' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#E0F7FA' }}>
            <FileSearch size={24} style={{ color: '#00BCD4' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.enRevision}</span>
            <span className="kpi-label">En Revisión</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#795548' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#EFEBE9' }}>
            <Pause size={24} style={{ color: '#795548' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.enEspera}</span>
            <span className="kpi-label">En Espera</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#9C27B0' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#F3E5F5' }}>
            <Send size={24} style={{ color: '#9C27B0' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.derivadasProbidad}</span>
            <span className="kpi-label">Derivadas Probidad</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#4CAF50' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#E8F5E9' }}>
            <CheckCircle size={24} style={{ color: '#4CAF50' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.completadas}</span>
            <span className="kpi-label">Completadas</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#FF5722' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#FBE9E7' }}>
            <AlertTriangle size={24} style={{ color: '#FF5722' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.vencidasCNC}</span>
            <span className="kpi-label">Vencidas CNC</span>
          </div>
        </div>

        <div className="stats-kpi-card" style={{ borderLeftColor: '#D32F2F' }}>
          <div className="kpi-icon" style={{ backgroundColor: '#FFCDD2' }}>
            <Calendar size={24} style={{ color: '#D32F2F' }} />
          </div>
          <div className="kpi-content">
            <span className="kpi-value">{stats.vencidasProbidad}</span>
            <span className="kpi-label">Vencidas Probidad</span>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="stats-charts-grid">
        {/* Distribución por Estado */}
        <div className="stats-chart-card">
          <h3><Clock size={18} /> Distribución por Estado</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={stats.porEstado}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={5}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
              >
                {stats.porEstado.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tendencia Mensual */}
        <div className="stats-chart-card">
          <h3><TrendingUp size={18} /> Tendencia Mensual</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats.porMes}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="casos" 
                stroke={colorModulo} 
                strokeWidth={3}
                dot={{ fill: colorModulo, strokeWidth: 2, r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Por Institución */}
        <div className="stats-chart-card stats-chart-wide">
          <h3><Building2 size={18} /> Top 10 Instituciones</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.porInstitucion} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={200} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="casos" fill={colorModulo} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Por Asesor */}
        {stats.porAsesor.length > 0 && (
          <div className="stats-chart-card">
            <h3><User size={18} /> Gestiones por Asesor</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.porAsesor}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="casos" fill={colorModulo} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default EstadisticasModulo;
